/**
 * WebGL Context Manager - Single renderer shared across all Three.js components
 * This eliminates multiple WebGL contexts which are expensive and cause performance issues
 */

import * as THREE from 'three';

class WebGLContextManager {
  private static instance: WebGLContextManager;
  private renderer: THREE.WebGLRenderer | null = null;
  private activeScenes: Map<string, THREE.Scene> = new Map();
  private activeCanvases: Map<string, HTMLCanvasElement> = new Map();
  private currentSceneId: string | null = null;
  private animationCallbacks: Map<string, (time: number, delta: number) => void> = new Map();
  private animationId: number | null = null;
  private clock = new THREE.Clock();

  private constructor() {}

  static getInstance(): WebGLContextManager {
    if (!WebGLContextManager.instance) {
      WebGLContextManager.instance = new WebGLContextManager();
    }
    return WebGLContextManager.instance;
  }

  /**
   * Initialize the shared WebGL renderer
   */
  initRenderer(canvas?: HTMLCanvasElement): THREE.WebGLRenderer {
    if (!this.renderer) {
      this.renderer = new THREE.WebGLRenderer({
        canvas,
        antialias: true,
        alpha: true,
        powerPreference: 'high-performance',
        stencil: false,
        depth: true,
      });
      
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      this.renderer.outputColorSpace = THREE.SRGBColorSpace;
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1;
      
      // Start the shared animation loop
      this.startAnimationLoop();
    }
    return this.renderer;
  }

  /**
   * Register a new scene with the manager
   */
  registerScene(
    id: string, 
    scene: THREE.Scene, 
    camera: THREE.Camera,
    canvas: HTMLCanvasElement,
    animationCallback?: (time: number, delta: number) => void
  ): void {
    this.activeScenes.set(id, scene);
    this.activeCanvases.set(id, canvas);
    
    if (animationCallback) {
      this.animationCallbacks.set(id, animationCallback);
    }

    // Initialize renderer with the first canvas
    if (!this.renderer) {
      this.initRenderer(canvas);
    }

    // Update renderer size to match canvas
    const rect = canvas.getBoundingClientRect();
    this.renderer!.setSize(rect.width, rect.height, false);
  }

  /**
   * Unregister a scene from the manager
   */
  unregisterScene(id: string): void {
    this.activeScenes.delete(id);
    this.activeCanvases.delete(id);
    this.animationCallbacks.delete(id);

    if (this.currentSceneId === id) {
      this.currentSceneId = null;
    }

    // Stop animation loop if no scenes remain
    if (this.activeScenes.size === 0 && this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  /**
   * Switch to rendering a specific scene
   */
  switchToScene(id: string, camera: THREE.Camera): void {
    const scene = this.activeScenes.get(id);
    const canvas = this.activeCanvases.get(id);
    
    if (!scene || !canvas || !this.renderer) return;

    this.currentSceneId = id;
    
    // Update canvas context
    const context = canvas.getContext('webgl2') || canvas.getContext('webgl');
    if (context && this.renderer.getContext() !== context) {
      // Update renderer to use new canvas
      const rect = canvas.getBoundingClientRect();
      this.renderer.setSize(rect.width, rect.height, false);
    }

    // Render the scene
    this.renderer.render(scene, camera);
  }

  /**
   * Render all visible scenes (for preview mode)
   */
  renderVisibleScenes(visibleSceneIds: string[]): void {
    if (!this.renderer) return;

    for (const id of visibleSceneIds) {
      const scene = this.activeScenes.get(id);
      const canvas = this.activeCanvases.get(id);
      
      if (scene && canvas) {
        const rect = canvas.getBoundingClientRect();
        this.renderer.setSize(rect.width, rect.height, false);
        
        // Create a basic camera for preview
        const camera = new THREE.PerspectiveCamera(75, rect.width / rect.height, 0.1, 1000);
        camera.position.z = 5;
        
        this.renderer.render(scene, camera);
      }
    }
  }

  /**
   * Start the shared animation loop
   */
  private startAnimationLoop(): void {
    if (this.animationId) return;

    const animate = () => {
      this.animationId = requestAnimationFrame(animate);
      
      const time = this.clock.getElapsedTime();
      const delta = this.clock.getDelta();

      // Update all registered animation callbacks
      this.animationCallbacks.forEach((callback, id) => {
        if (this.activeScenes.has(id)) {
          callback(time, delta);
        }
      });

      // Render current scene if available
      if (this.currentSceneId) {
        const scene = this.activeScenes.get(this.currentSceneId);
        const canvas = this.activeCanvases.get(this.currentSceneId);
        
        if (scene && canvas) {
          const rect = canvas.getBoundingClientRect();
          this.renderer!.setSize(rect.width, rect.height, false);
          
          // Use a default camera - components should provide their own
          const camera = new THREE.PerspectiveCamera(75, rect.width / rect.height, 0.1, 1000);
          camera.position.z = 5;
          
          this.renderer!.render(scene, camera);
        }
      }
    };

    animate();
  }

  /**
   * Update animation callback for a scene
   */
  updateAnimationCallback(id: string, callback: (time: number, delta: number) => void): void {
    this.animationCallbacks.set(id, callback);
  }

  /**
   * Pause animations for a specific scene
   */
  pauseScene(id: string): void {
    this.animationCallbacks.delete(id);
  }

  /**
   * Resume animations for a specific scene
   */
  resumeScene(id: string, callback: (time: number, delta: number) => void): void {
    this.animationCallbacks.set(id, callback);
  }

  /**
   * Get the shared renderer instance
   */
  getRenderer(): THREE.WebGLRenderer | null {
    return this.renderer;
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    if (this.renderer) {
      this.renderer.dispose();
      this.renderer = null;
    }

    this.activeScenes.clear();
    this.activeCanvases.clear();
    this.animationCallbacks.clear();
    this.currentSceneId = null;
  }
}

export default WebGLContextManager;